import { useWorkOrderChartPeriodIndicators } from '@/core/domain/workorder/WorkOrdersChart/WorkOrdersChart.hooks';
import { WorkOrderChartFilter } from '@/core/domain/workorder/WorkOrdersChart/WorkOrdersChart.types';
import { useChartTheme } from '@/hooks';
import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';
import { ResponsiveLine, Serie } from '@nivo/line';
import { Card, Typography } from 'antd';
import { useMemo } from 'react';
import {
  WorkOrderIndicatorOption,
  workorderIndicatorOptions,
} from '../options';

interface Props {
  filter?: WorkOrderChartFilter;
}

export default function WorkOrderIndicatorsChat(props: Props) {
  const chartTheme = useChartTheme();

  const { data, dataUpdatedAt } = useWorkOrderChartPeriodIndicators(
    props.filter,
  );

  function getOption(key: string) {
    return workorderIndicatorOptions.find(
      (item) => item.key === key,
    ) as WorkOrderIndicatorOption;
  }

  let chartData = useMemo(
    () =>
      data?.reduce((acc, { date, ...curr }) => {
        Object.keys(curr).forEach((key) => {
          if (acc.find((item) => item.id === key)) {
            acc = acc.map((item) =>
              item.id === key
                ? {
                    ...item,
                    data: [
                      ...item.data,
                      { x: date, y: curr[key as keyof typeof curr] },
                    ],
                  }
                : item,
            );
          } else {
            acc = [
              ...acc,
              {
                id: key,
                data: [{ x: date, y: curr[key as keyof typeof curr] }],
              },
            ];
          }
        });

        return acc;
      }, [] as Serie[]),
    [data],
  );

  chartData = chartData?.map((item) => {
    const option = getOption(item.id as string);
    return { ...item, id: option?.label, color: option?.colorVariant };
  });

  return (
    <Card style={{ borderRadius: 12 }}>
      <Typography.Title level={4}>
        Valor total - Período x Status
      </Typography.Title>

      <div style={{ width: '100%', height: 350 }}>
        <ResponsiveLine
          key={dataUpdatedAt}
          data={chartData || []}
          enableSlices="x"
          margin={{ top: 24, right: 24, bottom: 24, left: 70 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            reverse: false,
          }}
          yFormat={(value) => `${currencyFormatter(value)}`}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: 5,
            legend: 'Total de orçamentos por status',
            format: (value) => `${metricPrefixFormatter(+value)}`,
            legendOffset: -50,
            legendPosition: 'middle',
          }}
          enableGridX={false}
          pointColor={{ from: 'color' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          colors={(d) => d.color}
          useMesh
          theme={chartTheme}
        />
      </div>
    </Card>
  );
}
